import { attr, customElement, FASTElement, observable, volatile } from '@microsoft/fast-element';
import { Connect, logger, Message } from '@genesislcap/foundation-comms';
import { editTemplate } from './edit.template';
import { editStyles } from './edit.styles';
import { EntityManagement } from '@genesislcap/foundation-entity-management';
import { CASCADE_ENUM, EnumTranslate, IEnumService } from '../../../../services/enum.service';
import { ICommonService } from '../../../../services/common.service';
import { ICompanyService } from '../../../../services/company.service';
import { ICompany, ICompanyForm } from '../../../../types';
import { NumberField, TextField } from '@microsoft/fast-foundation';
import { Select } from '@genesislcap/foundation-ui';
import { clearErrorUtil, displayErrorUtil } from '../../../../utils/form';
import { updateArray } from '../../../../utils/dataserver';
import { formatDateForInput } from '../../../../utils/formatting';
import { Tabs } from '@genesislcap/foundation-zero';
import { AssignedUsers } from './assigned-users/assigned-users';
import { Route } from '@microsoft/fast-router';
import { Profile } from '../../../../utils/profiles';
import { PROFILE_RIGHTS, RoutePath } from '../../../../utils';
import { IUserService } from '../../../../services/user.service';
import { notifyError } from '../../../../utils/notify';

export enum COMPANY_ROLE {
  BI_DIRECTIONAL = 'BI_DIRECTIONAL',
  SELLER_ONLY = 'SELLER_ONLY',
  ADVISOR = 'ADVISOR',
  CASCADE_ADMIN = 'CASCADE_ADMIN',
}

@customElement({
  name: 'edit-company',
  template: editTemplate,
  styles: editStyles,
})
export class EditCompany extends FASTElement {
  @ICommonService commonService: ICommonService;
  @IEnumService enumService: IEnumService;
  @ICompanyService companyService: ICompanyService;
  @IUserService userService: IUserService;

  @attr entityID: string;
  @attr isEditable = true;
  @attr creditProfileChecked: boolean = false;
  @observable selectedCompany: ICompany;
  @observable public companies: Array<any> = [];

  @observable companyNameValue: string;
  @observable companyRoleValue: string;
  @observable companyLEIValue: string;
  @observable nonSefParticipantAgreementValue: any;
  @observable regulatoryStatusValue: string;
  @observable manualRegulatoryStatusValue: string;
  @observable companyPhoneNumberValue: string = '';
  @observable companyAddressStreetValue: string;
  @observable companyCreditProfile: string = null;
  @observable companyAddressCityValue: string;
  @observable companyAddressStateValue: string;
  @observable companyAddressZipCodeValue: string = '';
  @observable companyCountryValue: string = 'United States';
  @observable federalEntityIndicatorValue: boolean = false;
  @observable financialEntityIndicatorValue: boolean = false;
  @observable rfqNegotiationValue: boolean = false;
  @observable autoExecutionValue: boolean = false;
  @observable creditworthyInstitutionValue: boolean = false;
  @observable parentGuaranteeFromCreditworthyInstitutionValue: boolean = false;
  @observable postLettersOfCreditValue: boolean = false;
  @observable selectedEntity: string;
  @observable isSpvValue: boolean = false;
  @observable spvParentValue: string;
  @observable companyHasUser: boolean = false;
  @observable isEdit: boolean = true;
  @observable selectedCompanyForSefAgreement: any;

  @observable isAdmin: boolean = false;

  @observable companyTabs: Tabs;

  public companyNameElement: TextField;
  public companyRoleElement: Select;
  public nonSefParticipantAgreementElement: TextField;
  public companyLEIElement: TextField;
  public regulatoryStatusElement: Select;
  public manualRegulatoryStatusElement: TextField;
  public companyPhoneNumberElement: NumberField;
  public companyAddressStreetElement: TextField;
  public companyAddressZipCodeElement: TextField;
  public companyAddressCityElement: TextField;
  public companyCountryElement: Select;
  public sefCompanyElement: Select;
  public companyAddressStateElement: Select;
  public spvParentCompanyElement: Select;

  @observable companyRoleList: Array<EnumTranslate> = [];
  @observable regulatoryStatusList: Array<EnumTranslate> = [];
  @observable companyAddressStateList: Array<String> = [];
  @observable companyCountryList: Array<String> = [];
  @observable availableParentCompaniesList: Array<ICompany> = [];

  public parentElement: EntityManagement;
  public assignedUsers: AssignedUsers;

  private _requiredFormFields = [];

  public connectedCallback() {
    super.connectedCallback();
    this.loadData();
    this.isAdmin = this.userService.hasAdminEditUserRights();

    this.parentElement.addEventListener('edit-entity', this.editEntity.bind(this));

    this._requiredFormFields = [
      this.companyNameElement,
      this.companyRoleElement,
      this.companyLEIElement,
      this.regulatoryStatusElement,
      this.companyPhoneNumberElement,
      this.companyAddressStreetElement,
      this.companyAddressZipCodeElement,
      this.companyAddressCityElement,
      this.companyCountryElement,
      this.companyAddressStateElement,
    ];

    this._requiredFormFields.forEach((field) => {
      if (field === this.companyPhoneNumberElement) {
        field.addEventListener('blur', () => this.checkCompanyPhoneNumberValue());
        return;
      }

      if (field === this.companyAddressZipCodeElement) {
        field.addEventListener('blur', () => this.checkCompanyAddressZipCodeValue());
        return;
      }

      field.addEventListener('blur', () => this.checkFieldValidity(field));
    });

    this.manualRegulatoryStatusElement.addEventListener('blur', () =>
      this.checkFieldValidity(this.manualRegulatoryStatusElement),
    );
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this));
    this.observer.observe(this);
  }

  private observer: IntersectionObserver | null = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private handleIntersection(entries: IntersectionObserverEntry[]) {
    const isVisible = entries.some((entry) => entry.isIntersecting);
    if (!isVisible) {
      this.isEdit = false;
      this.companyHasUser = false;
      this.clear();
    }
  }

  public checkCompanyPhoneNumberValue() {
    clearErrorUtil(this.companyPhoneNumberElement);
    if (!this.companyPhoneNumberValue) {
      this.checkFieldValidity(this.companyPhoneNumberElement);
      return;
    }
    if (this.companyPhoneNumberValue.length != 10) {
      displayErrorUtil(this.companyPhoneNumberElement, '10 digits required!');
    }
  }

  public checkCompanyAddressZipCodeValue() {
    clearErrorUtil(this.companyAddressZipCodeElement);
    if (!this.companyAddressZipCodeValue) {
      this.checkFieldValidity(this.companyAddressZipCodeElement);
      return;
    }
    if (!/^\d{5}$/.test(this.companyAddressZipCodeValue)) {
        displayErrorUtil(this.companyAddressZipCodeElement, '5 digits required!');
    }
  }

  private checkFieldValidity(elm: TextField | Select | NumberField) {
    clearErrorUtil(elm);
    if (!elm.value) {
      displayErrorUtil(elm);
    }
  }

  @volatile
  get isFormValid() {
    if (!this.rfqNegotiationValue && !this.autoExecutionValue) {
      return true;
    }

    if (this.companyAddressZipCodeValue.length != 5 || this.companyPhoneNumberValue.length != 10) {
      return true;
    }

    if (
      this.regulatoryStatusValue === 'OTHER' &&
      (!this.manualRegulatoryStatusValue || this.manualRegulatoryStatusValue === '')
    ) {
      return true;
    }

    const requiredFields = [
      this.companyNameValue,
      this.companyRoleValue,
      this.companyLEIValue,
      this.regulatoryStatusValue,
      this.companyPhoneNumberValue,
      this.companyCreditProfile,
      this.companyAddressStreetValue,
      this.companyAddressZipCodeValue,
      this.companyAddressCityValue,
      this.companyCountryValue,
      this.companyAddressStateValue,
    ];
    if (this.isSpvValue) {
      requiredFields.push(this.spvParentValue);
    }

    return requiredFields.some((x) => !x);
  }

  async editEntity(e) {
    if (!e.detail) {
      this.isEdit = false;
      return;
    }

    this.isEdit = true;
    this.selectedCompany = e.detail;
    this.assignedUsers?.setCompany(this.selectedCompany);
    this.isAdmin = this.isAdmin || (
         !this.userService.hasAdvisorRights() ||
         (this.userService.getCurrentUser().COMPANY_ID == this.selectedCompany.CASCADE_COMPANY_ID)
    );
    this.companyNameValue = this.selectedCompany.NAME;
    this.companyRoleValue = this.selectedCompany.COMPANY_ROLE;
    if (this.selectedCompany.CREDIT_PROFILE){
        this.creditProfileChecked = true
    }
    this.companyCreditProfile = this.selectedCompany.CREDIT_PROFILE;
    this.nonSefParticipantAgreementValue = formatDateForInput(
      this.selectedCompany.NON_SEF_PARTICIPANT_AGREEMENT_CONSENT_DATE as number,
    );
    this.companyLEIValue = this.selectedCompany.COMPANY_LEI;
    this.regulatoryStatusValue = this.selectedCompany.REGULATORY_STATUS;
    this.manualRegulatoryStatusValue = this.selectedCompany.MANUAL_REGULATORY_STATUS;
    this.companyPhoneNumberValue = this.selectedCompany.PHONE_NUMBER
      ? String(this.selectedCompany.PHONE_NUMBER)
      : '';
    this.companyAddressStreetValue = this.selectedCompany.ADDRESS_STREET;
    this.companyAddressCityValue = this.selectedCompany.CITY_NAME;
    this.companyAddressStateValue = this.selectedCompany.STATE_NAME;
    this.companyAddressZipCodeValue = this.selectedCompany.ADDRESS_ZIP_CODE
      ? this.selectedCompany.ADDRESS_ZIP_CODE
      : '';
    this.companyCountryValue = this.selectedCompany.COUNTRY_NAME;
    this.federalEntityIndicatorValue = this.selectedCompany.FEDERAL_ENTITY_INDICATOR;
    this.financialEntityIndicatorValue = this.selectedCompany.FINANCIAL_ENTITY_INDICATOR;
    this.rfqNegotiationValue = this.selectedCompany.RFQ_NEGOTIATION_FLAG;
    this.autoExecutionValue = this.selectedCompany.AUTO_EXECUTION_FLAG;
    this.selectedEntity = this.selectedCompany[this.entityID];
    this.isSpvValue = this.selectedCompany.IS_SPV_FLAG;
    if (this.isSpvValue) {
      this.loadValidParentCompanies();
    }
  }

  async update() {
    const companyData: ICompanyForm = {
      NAME: this.companyNameValue,
      PHONE_NUMBER: +this.companyPhoneNumberValue,
      ADDRESS_STREET: this.companyAddressStreetValue,
      ADDRESS_ZIP_CODE: this.companyAddressZipCodeValue,
      CITY_NAME: this.companyAddressCityValue,
      STATE_NAME: this.companyAddressStateValue,
      CREDIT_PROFILE: this.companyCreditProfile,
      COUNTRY_NAME: this.companyCountryValue,
      CREDITWORTHY_INSTITUTION: this.creditworthyInstitutionValue,
      PARENT_GUARANTEE_FROM_CREDITHWORTHY_INSTITUTION:
        this.parentGuaranteeFromCreditworthyInstitutionValue,
      COMPANY_ROLE: this.companyRoleValue,
      NON_SEF_PARTICIPANT_AGREEMENT_CONSENT_DATE: this.nonSefParticipantAgreementValue,
      REGULATORY_STATUS: this.regulatoryStatusValue,
      MANUAL_REGULATORY_STATUS: this.manualRegulatoryStatusValue,
      COMPANY_LEI: this.companyLEIValue,
      FINANCIAL_ENTITY_INDICATOR: this.financialEntityIndicatorValue,
      FEDERAL_ENTITY_INDICATOR: this.federalEntityIndicatorValue,
      POST_LETTERS_OF_CREDIT: this.postLettersOfCreditValue,
      RFQ_NEGOTIATION_FLAG: this.rfqNegotiationValue,
      AUTO_EXECUTION_FLAG: this.autoExecutionValue,
      CASCADE_COMPANY_ID: this.selectedCompany
        ? this.selectedCompany.CASCADE_COMPANY_ID
        : undefined,
      IS_SPV_FLAG: this.isSpvValue,
      CASCADE_PARENT_COMPANY_ID: this.spvParentValue,
    };

    let upsertCompany;
    if (this.isEdit) {
      upsertCompany = await this.companyService.eventCompanyUpdate(companyData);
    } else {
      upsertCompany = await this.companyService.eventCompanyInsert(companyData);
    }

    const error = upsertCompany.ERROR || upsertCompany?.DETAILS?.ERROR;

    if (!error) {
      this.parentElement.closeModal();
    } else {
      notifyError(error[0]?.CODE, error[0]?.TEXT);
    }
  }

  public clear() {
    this.selectedCompany = undefined;
    this.companyNameValue = '';
    clearErrorUtil(this.companyNameElement);
    this.companyRoleValue = '';
    clearErrorUtil(this.companyRoleElement);
    this.companyLEIValue = '';
    clearErrorUtil(this.companyLEIElement);
    this.regulatoryStatusValue = '';
    clearErrorUtil(this.regulatoryStatusElement);
    this.manualRegulatoryStatusValue = '';
    clearErrorUtil(this.manualRegulatoryStatusElement);
    this.companyPhoneNumberValue = null;
    clearErrorUtil(this.companyPhoneNumberElement);
    this.companyAddressStreetValue = '';
    clearErrorUtil(this.companyAddressStreetElement);
    this.companyAddressCityValue = '';
    clearErrorUtil(this.companyAddressCityElement);
    this.companyAddressStateValue = '';
    clearErrorUtil(this.companyAddressStateElement);
    this.companyAddressZipCodeValue = null;
    clearErrorUtil(this.companyAddressZipCodeElement);
    this.companyCountryValue = 'United States';
    clearErrorUtil(this.companyCountryElement);
    this.companyCreditProfile = null;
    this.federalEntityIndicatorValue = false;
    this.financialEntityIndicatorValue = false;
    this.rfqNegotiationValue = false;
    this.autoExecutionValue = false;
    this.selectedEntity = '';
    this.isSpvValue = false;
    this.spvParentValue = null;
    this.nonSefParticipantAgreementValue = '';
  }

  public async loadData() {
    await this.getCountries();
    await this.getStates();
    await this.loadEnums();
    await this.loadCompanies();
  }

  private async getCountries() {
    await this.commonService.getAllCountries().then((response) => {
      if (response.REPLY) {
        this.companyCountryList = response.REPLY;
        setTimeout(() => {
          this.companyCountryElement.value = 'United States';
          this.companyCountryValue = 'United States';
        }, 100);
      }
    });
  }

  private async getStates() {
    await this.commonService.getAllStates().then((response) => {
      if (response.REPLY) {
        this.companyAddressStateList = response.REPLY;
      }
    });
  }

  private async loadEnums() {
    await this.enumService.getEnumValues(CASCADE_ENUM.COMPANY_ROLE).then((response) => {
      if (response.REPLY) {
        this.companyRoleList = response.REPLY.filter(
          (x) => x.ENUM_VALUE !== COMPANY_ROLE.CASCADE_ADMIN,
        );
      }
    });

    await this.enumService.getEnumValues(CASCADE_ENUM.REGULATORY_STATUS).then((response) => {
      if (response.REPLY) {
        this.regulatoryStatusList = response.REPLY;
      }
    });
  }

  private async loadCompanies() {
    const currentCompanyObject = {
      NAME: this.companyService.getCurrentCompany().NAME,
      CASCADE_COMPANY_ID: this.companyService.getCurrentCompany().CASCADE_COMPANY_ID,
    };

    await this.companyService
      .getMyCompanies(
        `CASCADE_PARENT_COMPANY_ID == '${
          this.companyService.getCurrentCompany().CASCADE_COMPANY_ID
        }' && SEF_PARTICIPANT_AGREEMENT_CONSENT == false`,
      )
      .subscribe({
        next: (streamMessage: Message) => {
          const companiesResultList = streamMessage.ROW ?? null;

          if (companiesResultList) {
            this.companies = updateArray(companiesResultList, this.companies);
          }

          this.companies.sort((x) => (x === currentCompanyObject ? -1 : 0));

          if (
            !this.companies.includes(currentCompanyObject) &&
            !this.companyService.getCurrentCompany().SEF_PARTICIPANT_AGREEMENT_CONSENT
          ) {
            this.companies.unshift(currentCompanyObject);
          }
          if (this.companies.length > 0) {
            this.selectedCompanyForSefAgreement = this.companies[0];
            if (this.sefCompanyElement) {
              this.sefCompanyElement.value = this.companies[0].CASCADE_COMPANY_ID;
            }
          } else {
            this.selectedCompanyForSefAgreement = null;
            if (this.sefCompanyElement) {
              this.sefCompanyElement.value = null;
            }
          }
        },
        error: (error: any) => {
          logger.error(error);
        },
      });
  }

  public changedCreditProfile(value: string) {
    if (this.companyCreditProfile == value && this.creditProfileChecked) {
      this.creditProfileChecked = false
      this.companyCreditProfile = null
    } else {
      this.creditProfileChecked = true
      this.companyCreditProfile = value
    }
  }

  regulatoryStatusValueChanged(oldValue, newValue) {
    clearErrorUtil(this.manualRegulatoryStatusElement);
  }

  companyRoleValueChanged() {
    if (this.companyRoleValue !== COMPANY_ROLE.SELLER_ONLY) {
      this.isSpvValue = false;
      this.spvParentValue = null;
      this.availableParentCompaniesList = [];
    } else {
      this.loadValidParentCompanies();
    }
  }

  public validateInputForDateElements(e) {
    const date: string = e.target.value;
    if (!date) {
      return;
    }
    const curYearInput = date.split('-', 1)[0];
    const curMonthInput = date.split('-')[1];
    const curDayInput = date.split('-')[2];
    if (curYearInput.length > 4) {
      this.nonSefParticipantAgreementValue =
        curYearInput.slice(0, -1) + '-' + curMonthInput + '-' + curDayInput;
    }
  }

  isSpvValueChanged() {
    if (!this.isSpvValue) {
      this.spvParentValue = null;
    }
  }

  private loadValidParentCompanies() {
    let criteria = '';
    if (this.companyRoleValue === COMPANY_ROLE.SELLER_ONLY) {
      criteria = `IS_SPV_FLAG != true && (COMPANY_ROLE == '${COMPANY_ROLE.BI_DIRECTIONAL}' || COMPANY_ROLE == '${COMPANY_ROLE.SELLER_ONLY}')`;
    }
    if (this.selectedCompany) {
      criteria += ` && CASCADE_COMPANY_ID != '${this.selectedCompany.CASCADE_COMPANY_ID}'`;
    }
    this.availableParentCompaniesList = [];
    this.companyService.getAllCompanies(criteria).subscribe({
      next: (streamMessage: Message) => {
        const companiesResultList = streamMessage.ROW ?? null;
        if (companiesResultList) {
          this.availableParentCompaniesList = updateArray(
            companiesResultList,
            this.availableParentCompaniesList,
          );
          if (this.selectedCompany?.CASCADE_PARENT_COMPANY_ID) {
            setTimeout(() => {
              this.spvParentValue = this.selectedCompany.CASCADE_PARENT_COMPANY_ID;
            }, 300);
          }
        }
      },
      error: (error: any) => {
        logger.error(error);
      },
    });
  }

  public viewSefAgreement() {
    const path = `${RoutePath.ACKNOWLEDGEMENT}/${this.selectedCompanyForSefAgreement.CASCADE_COMPANY_ID}`;
    Route.path.push(path);
  }

  public sefCompanySelected() {
    if (this.sefCompanyElement?.value) {
      this.selectedCompanyForSefAgreement = JSON.parse(this.sefCompanyElement.value);
    }
  }
}
